import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { Upload, GitMerge, PlusCircle, FileSpreadsheet, LayoutDashboard } from 'lucide-react';
import { useMe, type Role } from '../lib/me';

const NAV: { to: string; label: string; icon: typeof Upload; roles: Role[] }[] = [
  { to: '/overview', label: 'Overview', icon: LayoutDashboard, roles: ['admin', 'analyst'] },
  { to: '/upload', label: '1 · Rowcal Upload', icon: Upload, roles: ['admin', 'analyst', 'uploader'] },
  { to: '/mapping', label: '2 · GL Mapping', icon: GitMerge, roles: ['admin', 'analyst'] },
  { to: '/adjustments', label: '3 · Addbacks', icon: PlusCircle, roles: ['admin', 'analyst'] },
  { to: '/outputs', label: '4 · Outputs', icon: FileSpreadsheet, roles: ['admin', 'analyst'] },
];

export default function ProtectedLayout({ children }: { children: ReactNode }) {
  const me = useMe();
  const role = me.data?.role;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-denim text-white shadow-md">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-allstar-red rounded flex items-center justify-center font-heading font-bold">A</div>
            <div>
              <div className="font-heading font-bold tracking-wider text-lg leading-none">ALLSTAR</div>
              <div className="text-[10px] uppercase tracking-widest text-allstar-red font-semibold">Lender Reporting</div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {me.data && <span className="text-white/70">{me.data.email}</span>}
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>

      {me.isError && (
        <div className="bg-allstar-red text-white px-6 py-3 text-sm">
          {(me.error as Error).message}
        </div>
      )}

      <div className="flex-1 flex">
        <nav className="w-56 bg-white border-r border-allstar-gray flex-shrink-0">
          <ul className="py-4">
            {NAV.filter((n) => role && n.roles.includes(role)).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-4 transition-colors ${
                      isActive
                        ? 'border-allstar-red text-denim bg-allstar-gray-light'
                        : 'border-transparent text-allstar-gray-dark hover:bg-allstar-gray-light hover:text-denim'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="flex-1 p-6 overflow-x-auto">{role ? children : null}</main>
      </div>
    </div>
  );
}
